/**
 * @fileoverview Upload hook and provider for managing file uploads with real-time progress tracking.
 *
 * This module provides:
 * - Upload context and provider for managing upload state across the application
 * - TanStack Query integration for backend operations
 * - Web Worker integration for chunked file uploads
 *
 * Key features:
 * - Single upload per order with automatic queue management
 * - Robust cancellation at any stage (initialization, progress, completion)
 * - Error handling and retry functionality
 * - Backend synchronization for both real and fake repositories
 */
import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { auth0Client } from 'src/api/auth0_client';
import { FakeOrderRepository } from 'src/api/fake_order_repository';
import { OrderRepository } from 'src/api/order_repository';
import { notifyError } from 'src/components/toast';
import { getConfig } from 'src/config/config';
import { getRepoBackend } from 'src/config/environment';
import { orderKeys } from 'src/constants/orders';
import {
  useIsLdtSalesOrCustomerSuccessUser,
  useUser,
} from 'src/hooks/use_auth';
import {
  OrderBaseFieldsFragment,
  SlideImageScannerType,
  SlideMetadataInputType,
  UploadedFileCategory,
  UploadedFileStatus,
} from 'src/types';
import { RepoBackend } from 'src/types/env';

// Constants
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks

// Enums
export enum UploadStatus {
  InProgress = 'in-progress',
  Completed = 'completed',
  Failed = 'failed',
  Canceled = 'canceled',
  Queued = 'queued',
}

// Worker Communication Events
enum WorkerEventToWorker {
  Initialize = 'INITIALIZE_STARTED',
  UploadStart = 'UPLOAD_FILE_STARTED',
  ChunkUpload = 'CHUNK_UPLOAD_IN_PROGRESS',
  CancelUpload = 'UPLOAD_FILE_CANCELED',
  CompleteUpload = 'COMPLETE_UPLOAD',
}

enum WorkerEventFromWorker {
  WorkerInitialized = 'WORKER_INITIALIZED',
  UploadStarted = 'UPLOAD_STARTED',
  ChunkCompleted = 'CHUNK_UPLOAD_COMPLETED',
  ReadyToComplete = 'UPLOAD_READY_TO_COMPLETE',
  UploadCompleted = 'FILE_UPLOAD_COMPLETED',
  UploadFailed = 'UPLOAD_FILE_FAILED',
  CancelUpload = 'UPLOAD_FILE_CANCELED',
}

// Types
export type UploadCompleteCallback = (info: { orderId: string }) => void;

export interface UploadFileOptions {
  onComplete?: UploadCompleteCallback;
}

export interface UploadState {
  fileId: string;
  fileName: string;
  status: UploadStatus;
  progress: number;
  orderUrl: string;
  file: File;
  orderId: string;
  crossOrgId: string;
  scannerType?: SlideImageScannerType;
  category: UploadedFileCategory;
  slideMetadata?: SlideMetadataInputType;
  onComplete?: UploadCompleteCallback;
}

interface UploadContextProps {
  activeUpload: UploadState | null;
  queue: UploadState[];
  uploadFile: (
    file: File,
    orderId: string,
    crossOrgId: string,
    category: UploadedFileCategory,
    scannerType?: SlideImageScannerType,
    slideMetadata?: SlideMetadataInputType,
    options?: UploadFileOptions,
  ) => Promise<void>;
  retryUpload: (fileId: string) => Promise<void>;
  /** Cancel an in-progress/queued upload or delete a file on the backend. Single entry point for "remove file". */
  removeFileUpload: (fileId: string, orderId: string) => Promise<void>;
  /** Whether a file is currently being deleted or canceled on the backend. */
  isFileDeleting: (fileId: string) => boolean;
  isUploadingForOrder: (orderId: string) => boolean;
  getUploadForOrder: (orderId: string) => UploadState | null;
  getUploadsForOrder: (orderId: string) => UploadState[];
}

// Context
const UploadContext = createContext<UploadContextProps | undefined>(undefined);

// Hooks
export const useUpload = () => {
  const ctx = useContext(UploadContext);
  if (!ctx) throw new Error('useUpload must be used within UploadProvider');
  return ctx;
};

// Helper Functions
const createUploadState = (
  file: File,
  orderId: string,
  crossOrgId: string,
  organization: string,
  status: UploadStatus,
  category: UploadedFileCategory,
  scannerType?: SlideImageScannerType,
  fileId?: string,
  slideMetadata?: SlideMetadataInputType,
  onComplete?: UploadCompleteCallback,
): UploadState => ({
  fileId:
    fileId ||
    `queued-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
  fileName: file.name,
  progress: 0,
  orderUrl: `/${organization}/orders/${orderId}`,
  status,
  file,
  orderId,
  crossOrgId,
  scannerType,
  category,
  ...(slideMetadata !== undefined && { slideMetadata }),
  ...(onComplete !== undefined && { onComplete }),
});

const createFailedUpload = (
  file: File,
  orderId: string,
  crossOrgId: string,
  organization: string,
  category: UploadedFileCategory,
  scannerType?: SlideImageScannerType,
  fileId?: string,
  slideMetadata?: SlideMetadataInputType,
): UploadState =>
  createUploadState(
    file,
    orderId,
    crossOrgId,
    organization,
    UploadStatus.Failed,
    category,
    scannerType,
    fileId || `failed-${Date.now()}`,
    slideMetadata,
  );

// Check if fileId is client-side only (not sent to backend)
const isClientSideFileId = (fileId: string): boolean => {
  return (
    fileId.startsWith('temp-') ||
    fileId.startsWith('queued-') ||
    fileId.startsWith('failed-')
  );
};

// Provider Component
export const UploadProvider: React.FC<{
  children: React.ReactNode;
  orderRepository: OrderRepository;
  uploadWorker: Worker;
}> = ({ children, orderRepository, uploadWorker }) => {
  // Single state for all uploads - eliminates dual state complexity
  const [uploads, setUploads] = useState<UploadState[]>([]);
  /** File IDs currently being deleted or canceled on the backend (for loading UI). */
  const [deletingFileIds, setDeletingFileIds] = useState<Set<string>>(
    () => new Set(),
  );
  const workerRef = useRef<Worker | null>(null);
  const isUploadingRef = useRef<boolean>(false);
  const cancelingUploadsRef = useRef<Set<string>>(new Set());
  /** FileIds we've sent COMPLETE_UPLOAD for; don't call backend cancel for these */
  const completeSentForFileIdsRef = useRef<Set<string>>(new Set());
  /** User canceled these fileIds (map to orderId so we can delete when UPLOAD_COMPLETED arrives after overlay was already cleared) */
  const canceledByUserFileIdsRef = useRef<Map<string, string>>(new Map());
  /** Latest uploads so we can read orderId synchronously in message handler (setState updater may run later) */
  const uploadsRef = useRef<UploadState[]>([]);
  uploadsRef.current = uploads;
  /** User canceled during create (tempFileId -> orderId); let create finish then call cancelMultipartUpload */
  const canceledDuringCreateRef = useRef<Map<string, string>>(new Map());
  /** Synchronous map of real fileId -> metadata so the worker message handler can
   *  look them up without waiting for React to re-render uploadsRef after the tempFileId swap. */
  const uploadMetadataRef = useRef<
    Map<
      string,
      {
        orderId: string;
        category: UploadedFileCategory;
        onComplete?: UploadCompleteCallback;
      }
    >
  >(new Map());
  const { organization } = useParams();
  const user = useUser();
  const getAccessTokenSilently = useCallback(async () => {
    return await auth0Client.getTokenSilently();
  }, []);
  const isLdtSalesOrCustomerSuccessUser = useIsLdtSalesOrCustomerSuccessUser();
  const queryClient = useQueryClient();

  // Helper to get active upload (first in-progress upload)
  const getActiveUpload = useCallback(() => {
    return (
      uploads.find((upload) => upload.status === UploadStatus.InProgress) ||
      null
    );
  }, [uploads]);

  // Helper to get queued uploads (includes all non-active uploads)
  const getQueuedUploads = useCallback(() => {
    return uploads.filter(
      (upload) => upload.status !== UploadStatus.InProgress,
    );
  }, [uploads]);

  // Mutations for upload operations
  const createUploadMutation = useMutation({
    mutationFn: async (params: {
      filename: string;
      category: UploadedFileCategory;
      orderId: string;
      countParts: number;
      signal?: AbortSignal;
      slideMetadata?: SlideMetadataInputType;
      scannerType?: SlideImageScannerType;
    }) => {
      return orderRepository.createMultipartUpload(
        params.filename,
        params.category,
        params.orderId,
        params.countParts,
        params.signal,
        params.slideMetadata,
        params.scannerType,
      );
    },
    onSuccess: (fileId, variables) => {
      // Update cache directly instead of invalidating to avoid refetch
      queryClient.setQueryData(
        orderKeys.detail(variables.orderId),
        (oldOrder?: OrderBaseFieldsFragment | null) => {
          if (!oldOrder) return oldOrder;

          // Create the new uploaded file edge matching the GraphQL query structure
          // Use variables directly since upload state might still have tempFileId
          const newFileEdge = {
            __typename: 'UploadedFileEdge' as const,
            cursor: fileId,
            node: {
              __typename: 'UploadedFile' as const,
              id: fileId,
              filename: variables.filename,
              category: variables.category,
              status: UploadedFileStatus.InProgress,
              progress: 0,
              uploaderAuth0Id: user?.id,
            } as const,
          };

          // Add the new file to uploadedFiles.edges (avoid duplicates)
          const existingEdges = oldOrder.uploadedFiles?.edges || [];
          const fileExists = existingEdges.some(
            (edge) => edge?.node?.id === fileId,
          );

          if (fileExists) {
            return oldOrder;
          }

          return {
            ...oldOrder,
            uploadedFiles: {
              ...oldOrder.uploadedFiles,
              edges: [...existingEdges, newFileEdge],
            },
          } as OrderBaseFieldsFragment;
        },
      );
    },
    onError() {
      // Error handling is now done in startUpload function
      // This mutation error means createMultipartUpload failed
    },
  });

  const cancelUploadMutation = useMutation({
    mutationFn: async (params: { fileId: string; orderId: string }) => {
      await orderRepository.cancelUpload?.(params.fileId);
      return params;
    },
    retry: false,
    onSuccess: (params) => {
      queryClient.invalidateQueries({
        queryKey: orderKeys.detail(params.orderId),
      });
    },
    onError: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: orderKeys.detail(params.orderId),
      });
      notifyError('Failed to remove file');
    },
  });

  const deleteFileMutation = useMutation({
    mutationFn: async (params: { fileId: string; orderId: string }) => {
      await orderRepository.deleteFile(params.fileId);
      return params;
    },
    onSuccess: (params) => {
      queryClient.setQueryData(
        orderKeys.detail(params.orderId),
        (oldOrder: OrderBaseFieldsFragment | undefined) => {
          if (!oldOrder?.uploadedFiles?.edges) return oldOrder;
          return {
            ...oldOrder,
            uploadedFiles: {
              ...oldOrder.uploadedFiles,
              edges: oldOrder.uploadedFiles.edges.filter(
                (edge) => edge?.node?.id !== params.fileId,
              ),
            },
          } as OrderBaseFieldsFragment;
        },
      );
      // Clear deleting state in same tick as cache update; consumers that derive from cache stay in sync.
      setDeletingFileIds((prev) => {
        const next = new Set(prev);
        next.delete(params.fileId);
        return next;
      });
      // File changes can update needsAttention on the orders list
      void queryClient.invalidateQueries({
        queryKey: orderKeys.all,
        refetchType: 'all',
      });
    },
    onError: (_, params) => {
      queryClient.invalidateQueries({
        queryKey: orderKeys.detail(params.orderId),
      });
      notifyError('Failed to remove file');
      setDeletingFileIds((prev) => {
        const next = new Set(prev);
        next.delete(params.fileId);
        return next;
      });
    },
  });

  const completeUploadMutation = useMutation({
    mutationFn: async (params: {
      fileId: string;
      orderId: string;
      category: UploadedFileCategory;
      onComplete?: UploadCompleteCallback;
    }) => {
      // Only needed for fake repository
      if (getRepoBackend() === RepoBackend.Fake) {
        const fakeRepo = orderRepository as FakeOrderRepository;
        fakeRepo.completeUpload?.(params.fileId);
      }
      return params;
    },
    onSuccess: async (params) => {
      // Refetch order detail (file status) and lists (e.g. needsAttention, status, etc.).
      await queryClient.invalidateQueries({
        queryKey: orderKeys.detail(params.orderId),
      });
      await queryClient.invalidateQueries({
        queryKey: orderKeys.all,
        refetchType: 'all',
      });
      params.onComplete?.({ orderId: params.orderId });
    },
    onError: (error, params) => {
      // Mark the upload as failed if complete multipart upload fails
      setUploads((prev) =>
        prev.map((upload) =>
          upload.fileId === params.fileId
            ? { ...upload, status: UploadStatus.Failed }
            : upload,
        ),
      );
      Sentry.captureException(error);
    },
  });

  const { mutateAsync: completeUpload } = completeUploadMutation;
  const { mutateAsync: deleteFile } = deleteFileMutation;
  const { mutateAsync: createMultipartUpload } = createUploadMutation;
  const { mutateAsync: cancelMultipartUpload } = cancelUploadMutation;

  /** Internal: delete file on backend and track deleting state.
   * Used by removeFileUpload and UPLOAD_COMPLETED.
   */
  const deleteFileOnBackend = useCallback(
    async (fileId: string, orderId: string): Promise<void> => {
      setDeletingFileIds((prev) => new Set(prev).add(fileId));
      try {
        await deleteFile({ fileId, orderId });
      } catch (err) {
        setDeletingFileIds((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
        throw err;
      }
    },
    [deleteFile],
  );

  const createWorker = useCallback(async () => {
    // Reuse existing worker if it exists - don't terminate it
    if (workerRef.current) {
      return workerRef.current;
    }

    // Create new worker only if we don't have one
    const worker = uploadWorker;
    const token = await getAccessTokenSilently();

    await new Promise<void>((resolve, reject) => {
      const onInitMessage = (event: MessageEvent) => {
        const data =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data.eventName === WorkerEventFromWorker.WorkerInitialized) {
          worker.removeEventListener('message', onInitMessage);
          worker.removeEventListener('error', onError);
          resolve();
        }
      };

      const onError = (error: ErrorEvent) => {
        worker.removeEventListener('message', onInitMessage);
        worker.removeEventListener('error', onError);
        reject(error);
      };

      worker.addEventListener('message', onInitMessage);
      worker.addEventListener('error', onError);

      worker.postMessage({
        eventName: WorkerEventToWorker.Initialize,
        apiUrl: `${getConfig().apiBaseUrl}/graphql`,
        accessToken: token,
        organization: isLdtSalesOrCustomerSuccessUser ? organization : null,
      });
    });

    workerRef.current = worker;
    return worker;
  }, [
    uploadWorker,
    getAccessTokenSilently,
    isLdtSalesOrCustomerSuccessUser,
    organization,
  ]);

  const handleWorkerMessage = useCallback(
    async (
      event: MessageEvent,
      totalChunks: number,
      uploadChunk: (index: number) => Promise<void>,
      shouldCancel: { current: boolean },
      requestCompleteUpload: (fileId: string, fileName: string) => void,
    ) => {
      try {
        const { eventName, data } =
          typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        switch (eventName) {
          case WorkerEventFromWorker.WorkerInitialized:
            break;

          case WorkerEventFromWorker.UploadStarted:
            await uploadChunk(0);
            break;

          case WorkerEventFromWorker.ChunkCompleted: {
            const progress = Math.floor(
              ((data.chunkIndex + 1) / totalChunks) * 100,
            );
            setUploads((prev) =>
              prev.map((upload) =>
                upload.status === UploadStatus.InProgress
                  ? { ...upload, progress }
                  : upload,
              ),
            );

            const nextChunkIndex = data.chunkIndex + 1;
            if (nextChunkIndex < totalChunks && !shouldCancel.current) {
              await uploadChunk(nextChunkIndex);
            }
            break;
          }

          case WorkerEventFromWorker.ReadyToComplete:
            // Main thread gates complete: only send if we didn't already cancel.
            if (!shouldCancel.current && data.fileId && data.fileName) {
              requestCompleteUpload(data.fileId, data.fileName);
            }
            break;

          case WorkerEventFromWorker.UploadCompleted: {
            // completeMultipartUpload mutation just succeeded in the worker.
            const fileId = data.fileId;
            if (!fileId) break;
            completeSentForFileIdsRef.current.delete(fileId);
            const canceledOrderId =
              canceledByUserFileIdsRef.current.get(fileId);
            const wasCanceledByUser = canceledOrderId !== undefined;
            if (wasCanceledByUser)
              canceledByUserFileIdsRef.current.delete(fileId);

            // Use the synchronous metadata ref first (populated right after createMultipartUpload
            // resolves) so we don't depend on React having re-rendered uploadsRef yet.
            const metadata = uploadMetadataRef.current.get(fileId);
            uploadMetadataRef.current.delete(fileId);
            const orderId =
              metadata?.orderId ??
              uploadsRef.current.find((u) => u.fileId === fileId)?.orderId ??
              canceledOrderId;
            const category =
              metadata?.category ??
              uploadsRef.current.find((u) => u.fileId === fileId)?.category ??
              data.category;
            const onComplete =
              metadata?.onComplete ??
              uploadsRef.current.find((u) => u.fileId === fileId)?.onComplete;

            // Clear before flushing state: flushSync can run the queue effect
            // synchronously, and it must not see the slot as still busy.
            isUploadingRef.current = false;

            // Flush so leave-during-upload blockers see no pending uploads before
            // onComplete navigates (otherwise navigate is blocked then dropped).
            flushSync(() => {
              setUploads((prev) => prev.filter((u) => u.fileId !== fileId));
            });

            if (!orderId) break;
            if (wasCanceledByUser) {
              await deleteFileOnBackend(fileId, orderId);
            } else {
              await completeUpload({
                fileId,
                orderId,
                category,
                onComplete,
              });
            }
            break;
          }

          case WorkerEventFromWorker.CancelUpload:
            // Worker confirmed cancellation - remove from overlay
            setUploads((prev) =>
              prev.filter(
                (upload) =>
                  upload.fileId !== data.fileId &&
                  upload.status !== UploadStatus.InProgress,
              ),
            );
            shouldCancel.current = true;
            isUploadingRef.current = false;
            break;

          case WorkerEventFromWorker.UploadFailed:
            if (data.fileId) {
              completeSentForFileIdsRef.current.delete(data.fileId);
              canceledByUserFileIdsRef.current.delete(data.fileId);
            }
            setUploads((prev) => {
              const updatedUploads = prev.map((upload) => {
                // Try data.fileId first, then fall back to any in-progress upload
                if (
                  upload.fileId === data.fileId ||
                  (upload.status === UploadStatus.InProgress && !data.fileId)
                ) {
                  return { ...upload, status: UploadStatus.Failed };
                }
                return upload;
              });

              return updatedUploads;
            });
            shouldCancel.current = true;
            isUploadingRef.current = false;
            // Queue processing will be handled by useEffect watching uploads state
            break;

          default:
            throw new Error(`Unknown worker event: ${eventName}`);
        }
      } catch (err) {
        shouldCancel.current = true;
        Sentry.captureException(err);
      }
    },
    [completeUpload, deleteFileOnBackend],
  );

  // Helper to handle upload cancellation cleanup
  const handleUploadFailure = useCallback(
    (
      file: File,
      orderId: string,
      crossOrgId: string,
      tempFileId: string,
      category: UploadedFileCategory,
      scannerType?: SlideImageScannerType,
      slideMetadata?: SlideMetadataInputType,
    ) => {
      setUploads((prev) => [
        ...prev,
        createFailedUpload(
          file,
          orderId,
          crossOrgId,
          organization || '',
          category,
          scannerType,
          undefined,
          slideMetadata,
        ),
      ]);
      isUploadingRef.current = false;
      cancelingUploadsRef.current.delete(tempFileId);
    },
    [organization],
  );

  // Helper to check if upload should be canceled
  const createCancellationChecker = useCallback(
    (tempFileId: string, shouldCancelRef: { current: boolean }) => {
      return () => {
        if (cancelingUploadsRef.current.has(tempFileId)) {
          shouldCancelRef.current = true;
          setUploads((prev) =>
            prev.filter((upload) => upload.fileId !== tempFileId),
          );
          isUploadingRef.current = false;
          cancelingUploadsRef.current.delete(tempFileId);
          return true;
        }
        return false;
      };
    },
    [],
  );

  const startUpload = useCallback(
    async (
      file: File,
      orderId: string,
      crossOrgId: string,
      category: UploadedFileCategory,
      scannerType?: SlideImageScannerType,
      slideMetadata?: SlideMetadataInputType,
      options?: UploadFileOptions,
    ) => {
      if (isUploadingRef.current || !organization) return;
      isUploadingRef.current = true;

      const tempFileId = `temp-${Date.now()}`;
      const shouldCancelRef = { current: false };
      const checkCancellation = createCancellationChecker(
        tempFileId,
        shouldCancelRef,
      );

      // Initialize upload state
      // Single-file categories (only 1 file per order):
      //   - SlideImage: remove existing SlideImage uploads for this order
      //   - TestRequisitionForm: remove existing TestRequisitionForm uploads for this order
      // Multi-file categories (allow multiple files per order):
      //   - All other categories: keep all existing uploads
      const isSingleFileCategory = [
        UploadedFileCategory.SlideImage,
        UploadedFileCategory.TestRequisitionForm,
      ].includes(category);
      setUploads((prev) => {
        const filtered = isSingleFileCategory
          ? prev.filter(
              (item) =>
                // Keep uploads for different orders
                item.orderId !== orderId ||
                // Keep uploads for same order but different category
                item.category !== category,
            )
          : prev; // Keep all uploads for multi-file categories
        return [
          ...filtered,
          createUploadState(
            file,
            orderId,
            crossOrgId,
            organization,
            UploadStatus.Queued,
            category,
            scannerType,
            tempFileId,
            slideMetadata,
            options?.onComplete,
          ),
        ];
      });

      if (checkCancellation()) return;

      // Create worker with error handling
      let worker: Worker;
      try {
        worker = await createWorker();
      } catch (error) {
        Sentry.captureException(error);
        handleUploadFailure(
          file,
          orderId,
          crossOrgId,
          tempFileId,
          category,
          scannerType,
          slideMetadata,
        );
        return;
      }

      if (checkCancellation()) return;

      // Create multipart upload (we don't abort; if user canceled we'll cancel on backend after it resolves)
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      let fileId: string;
      try {
        fileId = await createMultipartUpload({
          filename: file.name,
          category: category,
          orderId,
          countParts: totalChunks,
          scannerType: scannerType,
          ...(slideMetadata !== undefined && { slideMetadata }),
          ...(scannerType !== undefined && { scannerType }),
        });
      } catch (error) {
        Sentry.captureException(error);
        setUploads((prev) =>
          prev.map((upload) =>
            upload.fileId === tempFileId
              ? { ...upload, status: UploadStatus.Failed }
              : upload,
          ),
        );
        isUploadingRef.current = false;
        return;
      }

      // User canceled during create: schedule backend cancel and don't upload any parts
      const canceledOrderId = canceledDuringCreateRef.current.get(tempFileId);
      if (canceledOrderId !== undefined) {
        canceledDuringCreateRef.current.delete(tempFileId);
        // Remove from cache immediately so we don't flash the file (create's onSuccess already added it)
        queryClient.setQueryData(
          orderKeys.detail(canceledOrderId),
          (oldOrder: OrderBaseFieldsFragment | undefined) => {
            if (!oldOrder?.uploadedFiles?.edges) return oldOrder;
            return {
              ...oldOrder,
              uploadedFiles: {
                ...oldOrder.uploadedFiles,
                edges: oldOrder.uploadedFiles.edges.filter(
                  (edge) => edge?.node?.id !== fileId,
                ),
              },
            } as OrderBaseFieldsFragment;
          },
        );
        try {
          await cancelMultipartUpload({ fileId, orderId: canceledOrderId });
        } finally {
          cancelingUploadsRef.current.delete(tempFileId);
        }
        isUploadingRef.current = false;
        return;
      }

      if (checkCancellation()) {
        cancelMultipartUpload({ fileId, orderId });
        isUploadingRef.current = false;
        return;
      }

      // Switch to InProgress with real fileId
      setUploads((prev) =>
        prev.map((upload) =>
          upload.fileId === tempFileId
            ? { ...upload, status: UploadStatus.InProgress, fileId }
            : upload,
        ),
      );

      // Register synchronously so handleWorkerMessage can find orderId/category without
      // waiting for React to re-render and update uploadsRef.
      uploadMetadataRef.current.set(fileId, {
        orderId,
        category,
        onComplete: options?.onComplete,
      });

      // Set up chunk upload function
      const uploadChunk = async (index: number) => {
        if (shouldCancelRef.current) return;
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBlob = file.slice(start, end, 'application/octet-stream');

        worker.postMessage({
          eventName: WorkerEventToWorker.ChunkUpload,
          fileId,
          fileName: file.name,
          chunkBlob,
          chunkIndex: index,
          isLastChunk: index === totalChunks - 1,
          accessToken: await getAccessTokenSilently(),
        });
      };

      const requestCompleteUpload = (
        completeFileId: string,
        completeFileName: string,
      ) => {
        if (shouldCancelRef.current) return;
        completeSentForFileIdsRef.current.add(completeFileId);
        worker.postMessage({
          eventName: WorkerEventToWorker.CompleteUpload,
          fileId: completeFileId,
          fileName: completeFileName,
        });
      };

      // Set up worker handlers
      worker.onmessage = async (event) =>
        handleWorkerMessage(
          event,
          totalChunks,
          uploadChunk,
          shouldCancelRef,
          requestCompleteUpload,
        );
      worker.onerror = () => {
        setUploads((prev) =>
          prev.map((upload) =>
            upload.fileId === fileId
              ? { ...upload, status: UploadStatus.Failed }
              : upload,
          ),
        );
        shouldCancelRef.current = true;
        isUploadingRef.current = false;
      };

      // Start upload
      worker.postMessage({
        eventName: WorkerEventToWorker.UploadStart,
        fileId,
        fileName: file.name,
        totalChunks,
        organization: isLdtSalesOrCustomerSuccessUser ? organization : null,
      });
    },
    [
      isLdtSalesOrCustomerSuccessUser,
      organization,
      createWorker,
      createMultipartUpload,
      handleWorkerMessage,
      createCancellationChecker,
      handleUploadFailure,
      cancelMultipartUpload,
      getAccessTokenSilently,
      queryClient,
    ],
  );

  const processQueue = useCallback(async () => {
    const activeUpload = getActiveUpload();
    const allNonActiveUploads = getQueuedUploads();
    // Only process uploads that are actually queued
    const queuedUploads = allNonActiveUploads.filter(
      (upload) => upload.status === UploadStatus.Queued,
    );

    if (!activeUpload && queuedUploads.length > 0 && !isUploadingRef.current) {
      const nextUpload = queuedUploads[0];

      // Only process uploads that are queued
      if (
        nextUpload.file &&
        nextUpload.orderId &&
        nextUpload.status === UploadStatus.Queued
      ) {
        // Remove the queued upload from state before starting it
        // This prevents duplicate entries when startUpload creates a new upload state
        setUploads((prev) =>
          prev.filter((upload) => upload.fileId !== nextUpload.fileId),
        );

        // Start the next upload
        await startUpload(
          nextUpload.file,
          nextUpload.orderId,
          nextUpload.crossOrgId,
          nextUpload.category,
          nextUpload.scannerType,
          nextUpload.slideMetadata,
          nextUpload.onComplete
            ? { onComplete: nextUpload.onComplete }
            : undefined,
        );
      }
    }
  }, [getActiveUpload, getQueuedUploads, startUpload]);

  useEffect(() => {
    // Auto-process queue when uploads state changes and no upload is currently in progress
    if (!isUploadingRef.current) {
      processQueue();
    }
  }, [uploads, processQueue]);

  const uploadFile = useCallback(
    async (
      file: File,
      orderId: string,
      crossOrgId: string,
      category: UploadedFileCategory,
      scannerType?: SlideImageScannerType,
      slideMetadata?: SlideMetadataInputType,
      options?: UploadFileOptions,
    ) => {
      const activeUpload = getActiveUpload();
      if (!activeUpload && !isUploadingRef.current) {
        await startUpload(
          file,
          orderId,
          crossOrgId,
          category,
          scannerType,
          slideMetadata,
          options,
        );
      } else {
        // Queue the upload
        const isSingleFileCategory = [
          UploadedFileCategory.SlideImage,
          UploadedFileCategory.TestRequisitionForm,
        ].includes(category);

        setUploads((prev) => {
          const filtered = isSingleFileCategory
            ? prev.filter(
                (item) =>
                  // Keep uploads for different orders
                  item.orderId !== orderId ||
                  // Keep uploads for same order but different category
                  item.category !== category,
              )
            : prev; // Keep all uploads for multi-file categories
          return [
            ...filtered,
            createUploadState(
              file,
              orderId,
              crossOrgId,
              organization || '',
              UploadStatus.Queued,
              category,
              scannerType,
              undefined,
              slideMetadata,
              options?.onComplete,
            ),
          ];
        });
      }
    },
    [getActiveUpload, organization, startUpload],
  );

  const isFileDeleting = useCallback(
    (fileId: string) => deletingFileIds.has(fileId),
    [deletingFileIds],
  );

  const retryUpload = useCallback(
    async (fileId: string) => {
      const allNonActiveUploads = getQueuedUploads();

      // Find the failed upload in non-active uploads
      const failedUpload = allNonActiveUploads.find(
        (item) => item.fileId === fileId,
      );

      if (failedUpload?.file && failedUpload.orderId) {
        // If there's already an active upload, queue this retry
        if (getActiveUpload() || isUploadingRef.current) {
          setUploads((prev) =>
            prev.map((upload) =>
              upload.fileId === fileId
                ? {
                    ...upload,
                    status: UploadStatus.Queued,
                    // Replace temp- fileId with queued- fileId so UI shows "Queued for upload" instead of "Uploading..."
                    fileId: upload.fileId.startsWith('temp-')
                      ? `queued-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
                      : upload.fileId,
                  }
                : upload,
            ),
          );
        } else {
          // No active upload, start this one immediately
          setUploads((prev) => prev.filter((item) => item.fileId !== fileId));
          await startUpload(
            failedUpload.file,
            failedUpload.orderId,
            failedUpload.crossOrgId,
            failedUpload.category,
            failedUpload.scannerType,
            failedUpload.slideMetadata,
          );
        }
      } else {
        Sentry.captureException(
          new Error('Failed upload not found or missing data'),
        );
      }
    },
    [getActiveUpload, getQueuedUploads, startUpload],
  );

  const isUploadingForOrder = useCallback(
    (orderId: string) => {
      const activeUpload = getActiveUpload();
      const queuedUploads = getQueuedUploads();

      // Check if there's an active upload for this order (including initializing)
      if (activeUpload?.orderId === orderId) {
        return (
          activeUpload.status === UploadStatus.Queued ||
          activeUpload.status === UploadStatus.InProgress
        );
      }

      // Check if there's a queued upload for this order
      return queuedUploads.some(
        (upload) =>
          upload.orderId === orderId && upload.status === UploadStatus.Queued,
      );
    },
    [getActiveUpload, getQueuedUploads],
  );

  const getUploadForOrder = useCallback(
    (orderId: string) => {
      const activeUpload = getActiveUpload();
      const queuedUploads = getQueuedUploads();

      // Check active upload first
      if (activeUpload?.orderId === orderId) {
        return activeUpload;
      }

      // Check queue
      return queuedUploads.find((upload) => upload.orderId === orderId) || null;
    },
    [getActiveUpload, getQueuedUploads],
  );

  const getUploadsForOrder = useCallback(
    (orderId: string) => {
      const allUploads = [
        ...(getActiveUpload() ? [getActiveUpload()!] : []),
        ...getQueuedUploads(),
      ];
      return allUploads.filter((upload) => upload.orderId === orderId);
    },
    [getActiveUpload, getQueuedUploads],
  );

  const removeFileUpload = useCallback(
    async (fileId: string, orderId: string): Promise<void> => {
      const activeUpload = getActiveUpload();
      const queuedUploads = getQueuedUploads();
      const upload =
        activeUpload?.fileId === fileId
          ? activeUpload
          : queuedUploads.find((item) => item.fileId === fileId);

      const inUploadState = upload != null;
      const orderIdForRef = upload?.orderId ?? orderId;

      if (!inUploadState) {
        // File not in upload state: delete or cancel on backend only.
        const order = queryClient.getQueryData<
          OrderBaseFieldsFragment | undefined
        >(orderKeys.detail(orderId));
        const fileEdge = order?.uploadedFiles?.edges?.find(
          (edge) => edge?.node?.id === fileId,
        );
        const isCompleted =
          fileEdge?.node?.status === UploadedFileStatus.Complete;
        if (isCompleted) {
          await deleteFileOnBackend(fileId, orderId);
        } else {
          setDeletingFileIds((prev) => new Set(prev).add(fileId));
          try {
            await cancelMultipartUpload({ fileId, orderId });
          } finally {
            setDeletingFileIds((prev) => {
              const next = new Set(prev);
              next.delete(fileId);
              return next;
            });
          }
        }
        return;
      }

      // File is in upload state: mark canceled, optional backend cancel, tell worker, remove from UI.
      if (cancelingUploadsRef.current.has(fileId)) return Promise.resolve();
      cancelingUploadsRef.current.add(fileId);

      if (orderIdForRef && !isClientSideFileId(fileId)) {
        canceledByUserFileIdsRef.current.set(fileId, orderIdForRef);
      }

      const tempFileIdToCheck = isClientSideFileId(fileId)
        ? fileId
        : upload?.fileId;
      if (
        tempFileIdToCheck &&
        isClientSideFileId(tempFileIdToCheck) &&
        orderIdForRef
      ) {
        canceledDuringCreateRef.current.set(tempFileIdToCheck, orderIdForRef);
      }

      if (
        orderIdForRef &&
        !isClientSideFileId(fileId) &&
        !completeSentForFileIdsRef.current.has(fileId)
      ) {
        (async () => {
          try {
            await cancelMultipartUpload({ fileId, orderId: orderIdForRef });
          } finally {
            cancelingUploadsRef.current.delete(fileId);
          }
        })();
      } else {
        cancelingUploadsRef.current.delete(fileId);
      }

      if (activeUpload?.fileId === fileId) {
        if (activeUpload.status === UploadStatus.InProgress) {
          workerRef.current?.postMessage({
            eventName: WorkerEventToWorker.CancelUpload,
            fileId,
          });
          isUploadingRef.current = false;
        } else {
          setUploads((prev) => prev.filter((item) => item.fileId !== fileId));
          isUploadingRef.current = false;
        }
      } else {
        setUploads((prev) => prev.filter((item) => item.fileId !== fileId));
      }
      return Promise.resolve();
    },
    [
      getActiveUpload,
      getQueuedUploads,
      cancelMultipartUpload,
      deleteFileOnBackend,
      queryClient,
    ],
  );

  return (
    <UploadContext.Provider
      value={{
        activeUpload: getActiveUpload(),
        queue: getQueuedUploads(),
        uploadFile,
        retryUpload,
        removeFileUpload,
        isFileDeleting,
        isUploadingForOrder,
        getUploadForOrder,
        getUploadsForOrder,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};
